import type { IvaTreatment } from '../../constants/ivaTreatment';
import { displayFiscalName } from '../../lib/displayName';
import type {
  AssistantConceptDraft,
  AssistantDraft,
  AssistantFieldStatus,
  AssistantQuestion,
} from '../../services/invoiceAssistant.service';
import { CURRENCIES, PAYMENT_FORMS } from '../constants/payment';
import {
  applyPaymentRules,
  createEmptyConcept,
  formatDraftAmount,
  PUBLIC_RECEIVER_RFC,
  type DraftConcept,
  type DraftPayment,
  type DraftReceiver,
  type InvoiceDraft,
} from '../lib/draft';

/**
 * El puente entre el borrador del asistente (llaves del DTO de emisión, con
 * estado por campo) y el del flujo nuevo. Puro: no sabe de React ni de red.
 *
 * Contrato del back: `rule`, `user` e `inferred` se aplican; `missing` y
 * `pending_taxes` NO, y el front no los rellena por su cuenta.
 */
const APPLIES: AssistantFieldStatus[] = ['rule', 'user', 'inferred'];

const RECEIVER_FIELDS = {
  rfc: 'rfc',
  name: 'name',
  tax_regime: 'taxRegime',
  postal_code: 'postalCode',
  usocfdi: 'usoCfdi',
} as const satisfies Record<string, keyof DraftReceiver>;

const PAYMENT_FIELDS = {
  payment_method: 'method',
  payment_form: 'form',
  currency: 'currency',
  exchange_rate: 'exchangeRate',
} as const satisfies Record<string, keyof DraftPayment>;

const CONCEPT_FIELDS = {
  product_code: 'satCode',
  unit_code: 'unitCode',
  quantity: 'quantity',
  description: 'description',
  unit_price: 'unitPrice',
  iva_treatment: 'ivaTreatment',
} as const satisfies Record<string, keyof DraftConcept>;

type ReceiverKey = keyof typeof RECEIVER_FIELDS;
type PaymentKey = keyof typeof PAYMENT_FIELDS;
type ConceptKey = keyof typeof CONCEPT_FIELDS;

const CONCEPT_PATH = /^concepts\[(\d+)\]\.(\w+)$/;
const conceptPath = (index: number, field: ConceptKey) => `concepts[${index}].${field}`;

const FIELD_LABELS: Record<string, string> = {
  'data.invoice_type': 'el tipo de comprobante',
  'data.payment_form': 'la forma de pago',
  'data.payment_method': 'el método de pago',
  'data.currency': 'la moneda',
  'data.exchange_rate': 'el tipo de cambio',
  'data.place_of_expedition': 'el lugar de expedición',
  'data.receiver.rfc': 'el RFC de tu cliente',
  'data.receiver.name': 'el nombre de tu cliente',
  'data.receiver.tax_regime': 'el régimen de tu cliente',
  'data.receiver.postal_code': 'el código postal de tu cliente',
  'data.receiver.usocfdi': 'el uso de CFDI',
  'data.receiver.residencia_fiscal': 'el país de residencia de tu cliente',
  'data.receiver.num_reg_id_trib': 'el número de registro fiscal de tu cliente',
};

const CONCEPT_LABELS: Record<ConceptKey, string> = {
  product_code: 'la clave del SAT',
  unit_code: 'la unidad',
  quantity: 'la cantidad',
  description: 'la descripción',
  unit_price: 'el precio',
  iva_treatment: 'el IVA',
};

const labelOf = (path: string) => {
  const concept = CONCEPT_PATH.exec(path);
  return concept ? (CONCEPT_LABELS[concept[2] as ConceptKey] ?? path) : (FIELD_LABELS[path] ?? path);
};

/** El valor de un campo del flujo, leído por la ruta del back. */
const readDraftField = (draft: InvoiceDraft, path: string): unknown => {
  const concept = CONCEPT_PATH.exec(path);
  if (concept) {
    const key = CONCEPT_FIELDS[concept[2] as ConceptKey];
    return key ? draft.concepts[Number(concept[1])]?.[key] : undefined;
  }
  if (path.startsWith('data.receiver.')) {
    const key = RECEIVER_FIELDS[path.slice('data.receiver.'.length) as ReceiverKey];
    return key ? draft.receiver?.[key] : undefined;
  }
  const key = PAYMENT_FIELDS[path.slice('data.'.length) as PaymentKey];
  return key ? draft.payment[key] : undefined;
};

const hasValue = (value: unknown) =>
  value !== undefined && value !== null && value !== '' && !(typeof value === 'number' && value <= 0);

const isBlankConcept = (concept: DraftConcept) =>
  !concept.satCode && !concept.description.trim() && concept.unitPrice <= 0;

export interface AppliedLine {
  text: string;
  /** Lo propuso el modelo: se marca para que el usuario lo confirme. */
  proposed: boolean;
}

/**
 * Aplica sobre el borrador del flujo lo que el asistente puede tocar, con las
 * mismas reglas del pago que una edición a mano. Devuelve el borrador nuevo y
 * lo que cambió, en palabras.
 */
export const applyAssistantDraft = (
  current: InvoiceDraft,
  turnDraft: AssistantDraft,
  unitNameOf: (code: string) => string,
): { next: InvoiceDraft; lines: AppliedLine[] } => {
  const status = turnDraft.field_status ?? {};
  // Un valor sin estado se trata como propuesto: marcar de más es inofensivo, aplicar a ciegas no.
  const statusOf = (path: string): AssistantFieldStatus => status[path] ?? 'inferred';
  const applies = (path: string, value: unknown) =>
    hasValue(value) && APPLIES.includes(statusOf(path));
  const proposed = (paths: string[]) => paths.some((path) => statusOf(path) === 'inferred');
  const lines: AppliedLine[] = [];

  let receiver = current.receiver;
  const source = turnDraft.data.receiver ?? {};
  const receiverPatch: Partial<DraftReceiver> = {};
  const receiverPaths: string[] = [];
  (Object.keys(RECEIVER_FIELDS) as ReceiverKey[]).forEach((field) => {
    const path = `data.receiver.${field}`;
    if (applies(path, source[field])) {
      receiverPatch[RECEIVER_FIELDS[field]] = source[field] as string;
      receiverPaths.push(path);
    }
  });
  if (receiverPaths.length > 0) {
    const rfc = receiverPatch.rfc ?? current.receiver?.rfc ?? '';
    // Otro RFC es otro cliente: no hereda nombre, régimen ni id del anterior.
    const base: DraftReceiver =
      current.receiver && current.receiver.rfc === rfc
        ? current.receiver
        : { mode: 'cliente', name: '', rfc: '', taxRegime: '', postalCode: '' };
    receiver = {
      ...base,
      ...receiverPatch,
      mode: rfc === PUBLIC_RECEIVER_RFC ? 'publico_general' : 'cliente',
    };
    if (receiver.rfc !== current.receiver?.rfc || receiver.name !== current.receiver?.name) {
      lines.push({
        text: `Cliente: ${[displayFiscalName(receiver.name), receiver.rfc].filter(Boolean).join(' · ')}`,
        proposed: proposed(receiverPaths),
      });
    }
  }

  const payment = { ...current.payment };
  (Object.keys(PAYMENT_FIELDS) as PaymentKey[]).forEach((field) => {
    const path = `data.${field}`;
    const value = turnDraft.data[field];
    if (!applies(path, value)) return;
    if (field === 'payment_method' && (value === 'PUE' || value === 'PPD')) payment.method = value;
    if (field === 'payment_form' && typeof value === 'string' && PAYMENT_FORMS[value]) payment.form = value;
    // Una moneda que el flujo no ofrece se deja a mano: forzarla rompería el selector.
    if (field === 'currency' && CURRENCIES.some((currency) => currency.code === value)) {
      payment.currency = value as DraftPayment['currency'];
    }
    if (field === 'exchange_rate' && typeof value === 'number') payment.exchangeRate = value;
  });
  if (payment.currency === 'MXN') payment.exchangeRate = undefined;
  const paymentLines: [boolean, string, string][] = [
    [payment.method !== current.payment.method, `Método: ${payment.method}`, 'data.payment_method'],
    [
      payment.form !== current.payment.form,
      `Forma de pago: ${PAYMENT_FORMS[payment.form] ?? payment.form}`,
      'data.payment_form',
    ],
    [payment.currency !== current.payment.currency, `Moneda: ${payment.currency}`, 'data.currency'],
    [
      payment.exchangeRate !== current.payment.exchangeRate && payment.exchangeRate !== undefined,
      `Tipo de cambio: ${payment.exchangeRate}`,
      'data.exchange_rate',
    ],
  ];
  paymentLines.forEach(([changed, text, path]) => {
    if (changed) lines.push({ text, proposed: proposed([path]) });
  });

  // Un solo concepto en blanco es el que trae toda factura nueva: se llena, no se deja atrás.
  const existing =
    current.concepts.length === 1 && isBlankConcept(current.concepts[0]) ? [] : current.concepts;
  const concepts = [...existing];
  turnDraft.concepts.forEach((incoming: AssistantConceptDraft, index) => {
    const patch: Partial<DraftConcept> = {};
    const paths: string[] = [];
    (Object.keys(CONCEPT_FIELDS) as ConceptKey[]).forEach((field) => {
      const path = conceptPath(index, field);
      const value = incoming[field];
      if (field === 'iva_treatment' && value === 'pending_taxes') return;
      if (!applies(path, value)) return;
      Object.assign(patch, { [CONCEPT_FIELDS[field]]: value });
      paths.push(path);
    });
    if (paths.length === 0) return;
    if (patch.unitCode) patch.unitName = unitNameOf(patch.unitCode);
    // El precio del back es el ValorUnitario del DTO: base sin IVA, no «lo que quiero recibir».
    if (patch.unitPrice !== undefined) patch.grossUp = false;
    const concept = { ...(concepts[index] ?? createEmptyConcept()), ...patch };
    concepts[index] = concept;
    lines.push({
      text: [
        concept.description || 'Concepto',
        concept.unitPrice > 0 && `${concept.quantity} × ${formatDraftAmount(concept.unitPrice)}`,
      ]
        .filter(Boolean)
        .join(' · '),
      proposed: proposed(paths),
    });
  });

  const next = applyPaymentRules(
    {
      ...current,
      receiver,
      payment,
      concepts: concepts.length > 0 ? concepts : current.concepts,
      updatedAt: new Date().toISOString(),
    },
    current,
  );
  return { next, lines };
};

/**
 * Lo que el usuario capturó, para que gane sobre lo que el modelo proponga.
 *
 * Sale un campo si el usuario lo cambió respecto a `baseline` (lo último que
 * aplicó el asistente, o el borrador vacío) o si el back ya lo tenía como
 * `user`: el back no guarda estado entre turnos, y dejar de mandarlo sería
 * olvidar lo que el usuario dijo.
 *
 * Los defaults del flujo (PUE, transferencia, pesos) NO salen: el back los
 * trataría como decisión del usuario y nunca podría inferir «en dólares».
 */
export const buildClientDraft = (
  current: InvoiceDraft,
  baseline: InvoiceDraft,
  lastStatus: Record<string, AssistantFieldStatus>,
): AssistantDraft | undefined => {
  const shouldSend = (path: string, value: unknown, before: unknown) =>
    hasValue(value) && (value !== before || lastStatus[path] === 'user');

  const data: AssistantDraft['data'] = {};
  (Object.keys(PAYMENT_FIELDS) as PaymentKey[]).forEach((field) => {
    const key = PAYMENT_FIELDS[field];
    if (shouldSend(`data.${field}`, current.payment[key], baseline.payment[key])) {
      Object.assign(data, { [field]: current.payment[key] });
    }
  });
  if (current.payment.currency === 'MXN') delete data.exchange_rate;

  const receiver: NonNullable<AssistantDraft['data']['receiver']> = {};
  (Object.keys(RECEIVER_FIELDS) as ReceiverKey[]).forEach((field) => {
    const key = RECEIVER_FIELDS[field];
    const value = current.receiver?.[key];
    if (shouldSend(`data.receiver.${field}`, value, baseline.receiver?.[key])) {
      receiver[field] = value as string;
    }
  });
  if (Object.keys(receiver).length > 0) data.receiver = receiver;

  const filled = current.concepts.filter((concept) => !isBlankConcept(concept));
  const touched = filled.some((concept, index) =>
    (Object.keys(CONCEPT_FIELDS) as ConceptKey[]).some((field) =>
      shouldSend(
        conceptPath(index, field),
        concept[CONCEPT_FIELDS[field]],
        baseline.concepts[index]?.[CONCEPT_FIELDS[field]],
      ),
    ),
  );
  // Si uno cambió van todos: el back empareja por índice y un hueco los desalinearía.
  const concepts: AssistantConceptDraft[] = touched
    ? filled.map((concept, index) => {
        const ivaPath = conceptPath(index, 'iva_treatment');
        const ivaUntouched = concept.ivaTreatment === baseline.concepts[index]?.ivaTreatment;
        const ivaWasOpen = ['missing', 'pending_taxes'].includes(lastStatus[ivaPath] ?? '');
        return {
          ...(concept.satCode ? { product_code: concept.satCode } : {}),
          unit_code: concept.unitCode,
          quantity: concept.quantity,
          description: concept.description,
          ...(concept.unitPrice > 0 ? { unit_price: concept.unitPrice } : {}),
          // El default del flujo (16%) no puede pasar por decisión del usuario sobre un IVA que Taxes no ha firmado.
          iva_treatment:
            ivaUntouched && ivaWasOpen ? 'pending_taxes' : (concept.ivaTreatment as IvaTreatment),
          retentions: [],
        };
      })
    : [];

  if (Object.keys(data).length === 0 && concepts.length === 0) return undefined;
  return { data, concepts };
};

/**
 * Por qué no se puede timbrar todavía, en una frase, o `null`.
 *
 * `pending_taxes` bloquea hasta que el asistente deje de reportarlo: es un
 * criterio que firma un contador, no un dato que falte. En una factura de
 * prueba no bloquea: sale al sandbox sin validez ante el SAT, y así el flujo
 * se puede recorrer completo antes de que Taxes firme. Un `missing` se libera
 * en cuanto el usuario lo llena a mano en el flujo, o con la pregunta opcional
 * correspondiente, en los dos modos.
 */
export const stampBlockReason = (
  status: Record<string, AssistantFieldStatus>,
  questions: AssistantQuestion[],
  current: InvoiceDraft,
  baseline: InvoiceDraft,
  isTestEmission: boolean,
): string | null => {
  const optional = new Set(questions.filter((question) => question.optional).map((q) => q.field));
  const pending = new Set<string>();
  const missing = new Set<string>();
  Object.entries(status).forEach(([path, fieldStatus]) => {
    if (fieldStatus === 'pending_taxes' && !isTestEmission) pending.add(labelOf(path));
    if (fieldStatus !== 'missing' || optional.has(path)) return;
    const now = readDraftField(current, path);
    const filledByHand = hasValue(now) && now !== readDraftField(baseline, path);
    if (!filledByHand) missing.add(labelOf(path));
  });

  const join = (labels: Set<string>) => {
    const list = [...labels];
    return list.length > 1 ? `${list.slice(0, -1).join(', ')} y ${list[list.length - 1]}` : list[0];
  };
  const reasons = [
    pending.size > 0 && `Un contador tiene que revisar ${join(pending)}.`,
    missing.size > 0 && `Falta ${join(missing)}: complétalo aquí o respóndele al asistente.`,
  ].filter(Boolean);
  return reasons.length > 0 ? reasons.join(' ') : null;
};
